import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Cpu, Speaker, HousePlug, Boxes, Bot, ArrowRight, Wrench,
  MapPin, CheckCircle2, MessageSquare, ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";

const IMG = {
  it: "https://images.unsplash.com/photo-1721333089073-215a56fd710c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw0fHxjb21wdXRlciUyMHJlcGFpciUyMHRlY2huaWNpYW58ZW58MHx8fHwxNzg3MjA4Njk0fDA&ixlib=rb-4.1.0&q=85",
  smart: "https://images.unsplash.com/photo-1558002038-1055907df827?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxzbWFydCUyMGhvbWUlMjBlbGVjdHJvbmljc3xlbnwwfHx8fDE3ODcyMDg2OTN8MA&ixlib=rb-4.1.0&q=85",
  mower: "https://images.unsplash.com/photo-1741326757602-186060c5d5b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxyb2JvdCUyMGxhd24lMjBtb3dlcnxlbnwwfHx8fDE3ODcyMDg2OTR8MA&ixlib=rb-4.1.0&q=85",
  hifi: "https://images.unsplash.com/photo-1609702847389-b8aec1b0b929?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxoaWdoJTIwZW5kJTIwYXVkaW8lMjBoaWZpfGVufDB8fHx8MTc4NzIwODY5M3ww&ixlib=rb-4.1.0&q=85",
  print: "https://images.unsplash.com/photo-1611117775350-ac3950990985?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHwzZCUyMHByaW50ZXIlMjBwcmludGluZ3xlbnwwfHx8fDE3ODcyMDg3MzN8MA&ixlib=rb-4.1.0&q=85",
};

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};

const PLANS = [
  { id: "2", years: 2, monthly_price: 149, popular: false,
    features: ["Årligt serviceeftersyn", "Rabat på reparationer", "Prioriteret support"] },
  { id: "3", years: 3, monthly_price: 129, popular: true,
    features: ["Alt i 2-års aftalen", "2 årlige eftersyn", "Gratis fejldiagnose"] },
  { id: "5", years: 5, monthly_price: 99, popular: false,
    features: ["Alt i 3-års aftalen", "Bedste månedspris", "Fri support hele året"] },
];

function ServiceCard({ icon: Icon, title, desc, img, className, testid }) {
  return (
    <motion.div
      variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}
      data-testid={testid}
      className={`group relative overflow-hidden rounded-2xl border border-slate-200 bg-white transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-200/70 ${className}`}
    >
      {img && (
        <div className="absolute inset-0">
          <img src={img} alt={title} className="h-full w-full object-cover opacity-[0.14] group-hover:opacity-25 transition-opacity duration-500" />
        </div>
      )}
      <div className="relative p-6 flex flex-col h-full">
        <span className="grid place-items-center h-11 w-11 rounded-xl bg-blue-600 text-white shadow-[0_0_18px_rgba(37,99,235,0.35)]">
          <Icon className="h-5 w-5" strokeWidth={2.2} />
        </span>
        <h3 className="mt-4 text-lg font-display font-bold tracking-tight text-slate-900">{title}</h3>
        <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{desc}</p>
      </div>
    </motion.div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(PLANS);
  const [discount, setDiscount] = useState(25);
  const [billing, setBilling] = useState("monthly");

  useEffect(() => {
    api.get("/service-plans").then(({ data }) => {
      if (data?.plans?.length) setPlans(data.plans);
      if (data?.annual_discount_pct != null) setDiscount(data.annual_discount_pct);
    }).catch(() => {});
  }, []);

  const annualPerYear = (m) => Math.round(m * 12 * (1 - discount / 100));
  const annualPerMonth = (m) => Math.round(m * (1 - discount / 100));

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden grain-bg">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.div variants={fade} initial="hidden" animate="show" custom={0}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-medium text-slate-600">
              <span className="h-2 w-2 rounded-full bg-blue-600 animate-pulse" />
              IT · HiFi · Hjemmeelektronik — ude & inde
            </motion.div>
            <motion.h1 variants={fade} initial="hidden" animate="show" custom={1}
              className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-display font-black tracking-tighter text-slate-900 leading-[1.02]">
              Alt inden for teknik,<br className="hidden sm:block" /> repareret & sat op rigtigt.
            </motion.h1>
            <motion.p variants={fade} initial="hidden" animate="show" custom={2}
              className="mt-5 text-base sm:text-lg text-slate-600 max-w-lg leading-relaxed">
              Få repareret, bygget, opsat og bliv vejledt i alt inden for IT, HiFi og hjemmeelektronik.
              Personlig service der møder dig, hvor du er.
            </motion.p>

            <motion.div variants={fade} initial="hidden" animate="show" custom={3}
              className="mt-7 rounded-2xl border border-blue-100 bg-white/70 px-6 py-5 inline-block">
              <p className="text-3xl sm:text-4xl font-display font-black tracking-tighter text-slate-900">
                Følger <span className="text-blue-600">DIT</span> behov.
              </p>
            </motion.div>

            <motion.div variants={fade} initial="hidden" animate="show" custom={4}
              className="mt-8 flex flex-wrap gap-3">
              <Button onClick={() => navigate("/booking")} data-testid="hero-book-btn"
                className="rounded-full h-12 px-7 text-base bg-blue-600 hover:bg-blue-700 shadow-[0_0_24px_rgba(37,99,235,0.4)]">
                Book service <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button onClick={() => document.getElementById("ydelser")?.scrollIntoView({ behavior: "smooth" })}
                variant="outline" data-testid="hero-services-btn"
                className="rounded-full h-12 px-7 text-base border-slate-300 hover:border-blue-600 hover:text-blue-600">
                Se ydelser
              </Button>
            </motion.div>
          </div>

          <motion.div variants={fade} initial="hidden" animate="show" custom={2} className="relative">
            <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-300/40">
              <img src={IMG.it} alt="IT-reparation" className="w-full h-[420px] object-cover" />
              <div className="absolute bottom-4 left-4 right-4 rounded-xl bg-white/90 backdrop-blur-md border border-slate-200 px-4 py-3 flex items-center gap-3">
                <span className="grid place-items-center h-9 w-9 rounded-lg bg-blue-600 text-white"><Wrench className="h-4 w-4" /></span>
                <div>
                  <p className="text-sm font-semibold text-slate-900">Hurtig & ærlig diagnose</p>
                  <p className="text-xs text-slate-500">Du får altid en klar vurdering før vi går i gang</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* VALUE STRIP */}
      <section className="border-y border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: MapPin, t: "Ude & inde", d: "Service hjemme hos dig" },
            { icon: CheckCircle2, t: "Ærlig pris", d: "Ingen skjulte gebyrer" },
            { icon: MessageSquare, t: "Personlig vejledning", d: "Vi taler dansk, ikke tech" },
            { icon: Wrench, t: "Bred erfaring", d: "IT, lyd & elektronik" },
          ].map((x) => (
            <div key={x.t} className="flex items-start gap-3">
              <x.icon className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-slate-900">{x.t}</p>
                <p className="text-xs text-slate-500">{x.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section id="ydelser" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-widest text-blue-600">Ydelser</p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-display font-black tracking-tighter text-slate-900">
            Ét sted til hele dit tekniske behov
          </h2>
          <p className="mt-3 text-slate-600">
            Fra en langsom computer til et komplet lydanlæg, smart home eller robotplæneklipper —
            jeg hjælper med reparation, opbygning, opsætning og vejledning.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-5 auto-rows-[minmax(180px,auto)]">
          <ServiceCard testid="service-it" icon={Cpu} img={IMG.it}
            title="IT-support & reparation"
            desc="Fejlfinding, opgraderinger, virusfjernelse, nybygning af PC og opsætning af netværk."
            className="md:col-span-7 md:row-span-2" />
          <ServiceCard testid="service-hifi" icon={Speaker} img={IMG.hifi}
            title="HiFi & lydanlæg"
            desc="Opsætning og optimering af stereo, surround og trådløs lyd i hjemmet."
            className="md:col-span-5" />
          <ServiceCard testid="service-smart" icon={HousePlug} img={IMG.smart}
            title="Smart home & hjemmeelektronik"
            desc="Installation og vejledning i smart-home, TV, routere og daglige gadgets."
            className="md:col-span-5" />
          <ServiceCard testid="service-print" icon={Boxes} img={IMG.print}
            title="3D-print"
            desc="Print af reservedele, prototyper og idéer — samt hjælp til din egen printer."
            className="md:col-span-6" />
          <ServiceCard testid="service-mower" icon={Bot} img={IMG.mower}
            title="Robotplæneklippere"
            desc="Opsætning, kabellægning, fejlfinding og klargøring af robotplæneklippere."
            className="md:col-span-6" />

          <motion.div
            variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}
            data-testid="service-serviceaftale"
            className="md:col-span-12 rounded-2xl border border-blue-200 bg-white p-6 sm:p-8">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="grid place-items-center h-11 w-11 rounded-xl bg-blue-600 text-white shadow-[0_0_18px_rgba(37,99,235,0.35)]">
                <ShieldCheck className="h-5 w-5" strokeWidth={2.2} />
              </span>
              <h3 className="text-xl font-display font-black tracking-tight text-slate-900">Serviceaftale</h3>
              <span className="rounded-full bg-blue-600 text-white text-xs font-semibold px-2.5 py-1">Mest attraktive</span>
            </div>
            <p className="mt-3 text-slate-600 leading-relaxed max-w-3xl">
              En attraktiv aftale på <span className="font-semibold text-slate-900">minimum 2 år</span>, hvor din enhed
              bliver serviceret og vedligeholdt til en lav pris i forhold til normal timepris — ifølge betingelserne.
              Kan tilkøbes til både <span className="font-semibold text-slate-900">nyt og brugt udstyr</span>.
              En standard serviceaftale følger altid med, når du køber en behovsbygget computer hos J-Computer.
            </p>

            <div className="mt-5 inline-flex items-center rounded-full border border-slate-200 bg-slate-50 p-1" data-testid="billing-toggle">
              <button type="button" onClick={() => setBilling("monthly")} data-testid="billing-monthly"
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${billing === "monthly" ? "bg-blue-600 text-white shadow" : "text-slate-600 hover:text-slate-900"}`}>
                Månedlig
              </button>
              <button type="button" onClick={() => setBilling("annual")} data-testid="billing-annual"
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${billing === "annual" ? "bg-blue-600 text-white shadow" : "text-slate-600 hover:text-slate-900"}`}>
                Årlig <span className={billing === "annual" ? "text-blue-100" : "text-blue-600"}>· spar {discount}%</span>
              </button>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              {plans.map((p) => (
                <div key={p.id} data-testid={`plan-${p.years}`}
                  className={`relative rounded-2xl border p-5 flex flex-col transition-transform duration-300 hover:-translate-y-1 ${p.popular ? "border-blue-600 bg-blue-50/70 shadow-lg shadow-blue-200/50" : "border-slate-200 bg-slate-50"}`}>
                  {p.popular && <span className="absolute -top-3 left-5 rounded-full bg-blue-600 text-white text-[11px] font-semibold px-2.5 py-0.5">Mest populær</span>}
                  <p className="text-sm font-semibold text-slate-500">{p.years} års aftale</p>
                  {billing === "monthly" ? (
                    <p className="mt-1 text-3xl font-display font-black tracking-tighter text-slate-900" data-testid={`plan-price-${p.years}`}>
                      {p.monthly_price}<span className="text-base font-bold text-slate-500"> kr./md</span>
                    </p>
                  ) : (
                    <div className="mt-1">
                      <p className="text-3xl font-display font-black tracking-tighter text-slate-900" data-testid={`plan-price-${p.years}`}>
                        {annualPerYear(p.monthly_price)}<span className="text-base font-bold text-slate-500"> kr./år</span>
                      </p>
                      <p className="text-xs text-blue-600 font-medium">= {annualPerMonth(p.monthly_price)} kr./md · spar {discount}%</p>
                    </div>
                  )}
                  <ul className="mt-4 space-y-2 text-sm text-slate-600 flex-1">
                    {(p.features || []).map((f) => (
                      <li key={f} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-blue-600 shrink-0" /> {f}</li>
                    ))}
                  </ul>
                  <Button onClick={() => navigate(`/booking?aftale=${p.years}&betaling=${billing === "annual" ? "aarlig" : "maanedlig"}`)} data-testid={`plan-select-${p.years}`}
                    className={`mt-5 rounded-full h-10 ${p.popular ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-white border border-slate-300 text-slate-800 hover:border-blue-600 hover:text-blue-600"}`}>
                    Vælg {p.years} år
                  </Button>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-slate-500">
              Alle aftaler gælder nyt og brugt udstyr. En standardaftale følger altid med en behovsbygget computer.
              Priser er vejledende og ifølge betingelserne.
            </p>
            <button type="button" onClick={() => navigate("/betingelser")} data-testid="landing-terms-link"
              className="mt-2 text-sm font-semibold text-blue-600 hover:underline">
              Læs de fulde betingelser →
            </button>
          </motion.div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="om" className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative rounded-3xl overflow-hidden border border-slate-700">
            <img src={IMG.smart} alt="Om J-Computer" className="w-full h-[380px] object-cover" />
          </div>
          <div>
            <p className="text-sm font-semibold uppercase tracking-widest text-blue-400">Om J-Computer</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-display font-black tracking-tighter">
              Teknik forklaret i øjenhøjde
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed">
              J-Computer er din lokale tekniske hjælper. Jeg brænder for at gøre teknologi
              nem og tilgængelig — uanset om det er en computer, der driller, et lydanlæg der skal
              lyde perfekt, eller en robotplæneklipper der skal sættes op.
            </p>
            <p className="mt-4 text-slate-300 leading-relaxed">
              Jeg møder dig med tålmodighed, ærlighed og en klar plan. Du bestemmer tempoet —
              jeg <span className="text-blue-400 font-semibold">følger DIT behov.</span>
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4">
              {[["100%", "Ærlig rådgivning"], ["Ude+Inde", "Fleksibel service"], ["Alt-i-én", "IT · HiFi · Elektronik"]].map(([n, l]) => (
                <div key={l} className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
                  <p className="text-xl font-display font-black text-white">{n}</p>
                  <p className="text-xs text-slate-400 mt-1">{l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
        <div className="relative overflow-hidden rounded-3xl bg-blue-600 px-8 py-14 sm:px-14 sm:py-20 text-center">
          <div className="grain-bg absolute inset-0 opacity-40" />
          <div className="relative">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-black tracking-tighter text-white">
              Klar til at få løst dit tekniske problem?
            </h2>
            <p className="mt-4 text-blue-100 max-w-xl mx-auto">
              Book en reparation eller service på få minutter. Du får svar direkte fra mig,
              og kan følge det hele i din egen kundeportal.
            </p>
            <Button onClick={() => navigate("/booking")} data-testid="cta-book-btn"
              className="mt-8 rounded-full h-12 px-8 text-base bg-white text-blue-700 hover:bg-slate-100">
              Book service nu <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
